import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import * as THREE from 'three'; // Fallback for Three.js
import { addcomponent } from '../../utils/imagePath';
import { debounce, throttle } from 'lodash';
import {
  EnhancedSlider,
  GridDropdown,
  ToggleCheckbox,
} from '../../utils/gridUtils';
import { handleAudioUpload } from '../gridpagefunction/Audio';
import { useSelector } from 'react-redux';
// import { handleAudioUpload } from '../../gridpagefunction/Audio';

/** Ensure a Three.js object's children array is mutable (not frozen/sealed). */
function ensureChildrenExtensible(obj) {
  if (obj?.children && !Object.isExtensible(obj.children)) {
    obj.children = [...obj.children];
  }
}

const AudioComponent = ({ completeAudioObject }) => {
  const window_type = useSelector((state) => state.window?.window_type);
  const [selectedObject, setSelectedObject] = useState(
    completeAudioObject?.selectedObject
  );
  useEffect(() => {
    if (completeAudioObject?.selectedObject) {
      setSelectedObject(completeAudioObject.selectedObject);
    }
  }, [completeAudioObject]);

  const stereoPannerRef = useRef(null);

  const initialSettings = useMemo(() => {
    if (!selectedObject) return null;

    const settings =
      selectedObject?.audio_settings ||
      selectedObject?.userData?.audio_settings ||
      {};
    return {
      checkboxes: {
        Mute: settings?.mute ?? false,
        'Bypass Effects': settings?.bypass_effects ?? false,
        'Bypass Listener Effects': settings?.bypass_listener_effects ?? false,
        'Bypass Reverb Zones': settings?.bypass_reverb_zones ?? false,
        'Play On Awake': settings?.play_on_awake ?? true,
        Loop: settings?.loop ?? false,
      },
      sliders: {
        Priority: settings?.priority ?? 128,
        Volume: Number(((settings?.volume ?? 0.5) * 100).toFixed(2)),
        Pitch: Number(((((settings?.pitch ?? 1) + 3) / 6) * 100).toFixed(2)),
        'Stereo Pan': Number(
          ((settings?.stereo_pan ?? 0) * 50 + 50).toFixed(2)
        ),
        'Spatial Blend': Number(
          ((settings?.spatial_blend ?? 0) * 100).toFixed(2)
        ),
        'Reverb Zone Mix': Number(
          (((settings?.reverb_zone_mix ?? 1) / 1.1) * 100).toFixed(2)
        ),
      },
      spatialSound: settings?.spatial_sound ?? 'Select a parameter',
      audioSource:
        selectedObject?.asset_file_path ||
        selectedObject?.userData?.audio_settings?.audio_source ||
        selectedObject?.objectURL ||
        selectedObject?.name ||
        settings?.audio_source ||
        'None (Audio Clip)',
    };
  }, [
    selectedObject?.uuid,
    selectedObject?.asset_file_path,
    selectedObject?.objectURL,
  ]);

  // Refs
  const audioListenerRef = useRef(null);
  const audioLoader = useRef(null);
  const fileInputRef = useRef(null);

  const prevSettingsRef = useRef(null);

  // State
  const [isAudioSourceValid, setIsAudioSourceValid] = useState(false);
  const [slidersWithTooltip, setSlidersWithTooltip] = useState({});

  // Debounced slider state
  const [sliders, setSliders] = useState(() => initialSettings?.sliders || {});
  const liveSlidersRef = useRef(sliders);
  const [liveSliders, setLiveSliders] = useState(sliders);

  const debouncedSetSliders = useMemo(
    () =>
      debounce((newSliders) => {
        setSliders(newSliders);
      }, 300),
    []
  );

  const [checkboxes, setCheckboxes] = useState(
    () => initialSettings?.checkboxes || {}
  );
  const [spatialSound, setSpatialSound] = useState(
    () => initialSettings?.spatialSound || 'Select a parameter'
  );
  const [audioSource, setAudioSource] = useState(
    () => initialSettings?.audioSource || 'None (Audio Clip)'
  );

  // Sync state with selectedObject when it changes
  useEffect(() => {
    if (!initialSettings) return;
    setCheckboxes(initialSettings.checkboxes);
    setSliders(initialSettings.sliders);
    setLiveSliders(initialSettings.sliders);
    setSpatialSound(initialSettings.spatialSound);
    setAudioSource(initialSettings.audioSource);
  }, [
    initialSettings,
    selectedObject?.asset_file_path,
    selectedObject?.objectURL,
  ]);

  const updateObjectSettings = useCallback(
    debounce((newSettings) => {
      if (!selectedObject) return;
      if (
        JSON.stringify(prevSettingsRef.current) === JSON.stringify(newSettings)
      ) {
        return;
      }
      
      // Ensure selectedObject.userData is writable
      if (!selectedObject.userData || Object.isFrozen(selectedObject.userData)) {
        try {
          selectedObject.userData = { ...(selectedObject.userData || {}) };
        } catch (e) {
          console.warn('Could not clone selectedObject.userData in AudioComponent:', e);
        }
      }

      try {
        selectedObject.audio_settings = newSettings;
      } catch (e) {
        console.warn('Could not set selectedObject.audio_settings:', e);
      }

      try {
        selectedObject.userData.audio_settings = { ...newSettings };
      } catch (e) {
        console.warn('Could not set selectedObject.userData.audio_settings:', e);
      }

      prevSettingsRef.current = newSettings;
    }, 300),
    [selectedObject]
  );

  useEffect(() => {
    const newSettings = {
      spatial_sound: spatialSound,
      mute: checkboxes.Mute,
      bypass_effects: checkboxes['Bypass Effects'],
      bypass_listener_effects: checkboxes['Bypass Listener Effects'],
      bypass_reverb_zones: checkboxes['Bypass Reverb Zones'],
      play_on_awake: checkboxes['Play On Awake'],
      loop: checkboxes.Loop,
      priority: sliders.Priority,
      volume: Number((sliders.Volume / 100).toFixed(3)),
      pitch: Number(((sliders.Pitch / 100) * 6 - 3).toFixed(3)),
      stereo_pan: Number((sliders['Stereo Pan'] / 50 - 1).toFixed(3)),
      spatial_blend: Number((sliders['Spatial Blend'] / 100).toFixed(3)),
      reverb_zone_mix: Number(
        ((sliders['Reverb Zone Mix'] / 100) * 1.1).toFixed(3)
      ),
    };
    updateObjectSettings(newSettings);
  }, [checkboxes, sliders, spatialSound, audioSource, updateObjectSettings]);

  const validateAudioSource = useCallback(
    debounce((url) => {
      if (!url) return;

      const validExtensions = ['.mp3', '.wav'];

      if (typeof url === 'string' && url.startsWith('blob:')) {
        setIsAudioSourceValid(true);
        return;
      }

      const fileExtension = url?.toLowerCase()?.slice(url?.lastIndexOf('.'));
      if (url.startsWith('http') && validExtensions.includes(fileExtension)) {
        fetch(url, { method: 'HEAD' })
          .then((response) => {
            if (response.ok) {
              setIsAudioSourceValid(true);
            } else {
              completeAudioObject.toast?.error(
                'Audio file is inaccessible. Please check the URL.'
              );
              setIsAudioSourceValid(false);
            }
          })
          .catch(() => {
            completeAudioObject.toast?.error('Failed to verify audio file.');
            setIsAudioSourceValid(false);
          });
      } else {
        setIsAudioSourceValid(false);
      }
    }, 500),
    [completeAudioObject?.toast]
  );

  useEffect(() => {
    if (selectedObject) {
      validateAudioSource(audioSource);
    }
  }, [selectedObject?.name, validateAudioSource, audioSource]);

  window.addEventListener(
    'click',
    () => {
      const context = THREE.AudioContext.getContext?.() || THREE.AudioContext;
      if (context && context.state === 'suspended') {
        context.resume();
      }
    },
    { once: true }
  );

  useEffect(() => {
    if (
      completeAudioObject &&
      (completeAudioObject.Three || THREE) &&
      !audioLoader.current
    ) {
      const ThreeLib = completeAudioObject.Three || THREE;
      audioLoader.current = new ThreeLib.AudioLoader();
    }
  }, [completeAudioObject]);

  const getVolumeFromSlider = useCallback(
    (value) => Math.round((value / 100) * 100) / 100,
    []
  );

  const updateAudioParameters = useCallback(
    throttle(() => {
      if (!selectedObject || !isAudioSourceValid) return;
      const targetAudio = selectedObject.getObjectByName(
        `audio_${selectedObject.uuid}`
      );
      if (!targetAudio) return;

      targetAudio.setLoop(checkboxes.Loop);
      targetAudio.setVolume(
        checkboxes.Mute ? 0 : getVolumeFromSlider(sliders.Volume)
      );
      const spatialBlendValue = sliders['Spatial Blend'] || 0;
      const isPositional =
        targetAudio instanceof
        (completeAudioObject.Three || THREE).PositionalAudio;

      const pitchValue = (sliders.Pitch / 100) * 6 - 3;
      if (targetAudio.source && targetAudio.source.buffer) {
        const buffer = targetAudio.source.buffer;
        const isReversed = buffer._reversed === true;
        const audioContext = targetAudio.context;
        let playbackOffset = 0;
        if (
          targetAudio.isPlaying &&
          targetAudio.source &&
          targetAudio.source.buffer
        ) {
          try {
            const now = audioContext.currentTime;
            if (targetAudio._startedAt) {
              playbackOffset = now - targetAudio._startedAt;
            }
          } catch (e) {
            playbackOffset = 0;
          }
        }
        if (pitchValue > 0) {
          if (isReversed && targetAudio.source._originalBuffer) {
            const duration = targetAudio.source._originalBuffer.duration;
            swapAudioSource(
              targetAudio,
              targetAudio.source._originalBuffer,
              pitchValue,
              targetAudio.isPlaying,
              audioContext,
              duration - playbackOffset
            );
          } else {
            targetAudio.setPlaybackRate(pitchValue);
            if (!targetAudio.isPlaying && audioSource !== 'None (Audio Clip)') {
              targetAudio.play();
            }
          }
        } else if (pitchValue < 0) {
          if (!isReversed) {
            if (!targetAudio.source._originalBuffer) {
              targetAudio.source._originalBuffer = buffer;
            }
            const reversedBuffer = cloneAudioBuffer(buffer, audioContext);
            for (let i = 0; i < reversedBuffer.numberOfChannels; i++) {
              Array.prototype.reverse.call(reversedBuffer.getChannelData(i));
            }
            reversedBuffer._reversed = true;
            const duration = reversedBuffer.duration;
            swapAudioSource(
              targetAudio,
              reversedBuffer,
              Math.abs(pitchValue),
              targetAudio.isPlaying,
              audioContext,
              duration - playbackOffset
            );
          } else {
            targetAudio.setPlaybackRate(Math.abs(pitchValue));
            if (!targetAudio.isPlaying && audioSource !== 'None (Audio Clip)') {
              targetAudio.play();
            }
          }
        } else {
          // pitchValue === 0: reset to normal playback (rate 1.0).
          // If the buffer was previously reversed, restore the original forward buffer.
          if (isReversed && targetAudio.source._originalBuffer) {
            swapAudioSource(
              targetAudio,
              targetAudio.source._originalBuffer,
              1,
              targetAudio.isPlaying,
              audioContext,
              targetAudio.source._originalBuffer.duration - playbackOffset
            );
          } else {
            targetAudio.setPlaybackRate(1);
            if (!targetAudio.isPlaying && audioSource !== 'None (Audio Clip)') {
              targetAudio.play();
            }
          }
        }
      } else {
        // No buffer loaded yet — still apply rate. pitch === 0 means normal speed (rate 1).
        targetAudio.setPlaybackRate(pitchValue === 0 ? 1 : Math.abs(pitchValue));
        if (!targetAudio.isPlaying && audioSource !== 'None (Audio Clip)') {
          targetAudio.play();
        }
      }

      const reverbIntensity = (sliders['Reverb Zone Mix'] / 100) * 1.1;
      if (
        targetAudio.reverb &&
        typeof targetAudio.reverb.setWet === 'function'
      ) {
        if (checkboxes['Bypass Reverb Zones']) {
          targetAudio.reverb.setWet(0);
        } else {
          targetAudio.reverb.setWet(reverbIntensity);
          if ('decayTime' in targetAudio.reverb) {
            if (spatialSound === 'Short') {
              targetAudio.reverb.decayTime = 0.8;
            } else if (spatialSound === 'Medium') {
              targetAudio.reverb.decayTime = 1.8;
            } else if (spatialSound === 'Long') {
              targetAudio.reverb.decayTime = 3.5;
            }
          }
        }
      }
      if (isPositional) {
        const blend = spatialBlendValue;
        targetAudio.setRefDistance(1 + 9 * blend);
        targetAudio.setRolloffFactor(blend);
        if (blend <= 0) {
          targetAudio.setDistanceModel('linear');
          targetAudio.setMaxDistance(10000);
          targetAudio.setRolloffFactor(0);
        } else {
          targetAudio.setDistanceModel('inverse');
          targetAudio.setMaxDistance(10000);
        }
        if (blend < 1 && stereoPannerRef.current) {
          const stereoPan = (sliders['Stereo Pan'] / 50 - 1) * (1 - blend);
          stereoPannerRef.current.pan.setValueAtTime(
            stereoPan,
            targetAudio.context.currentTime
          );
        }
      } else {
        if (stereoPannerRef.current) {
          const stereoPan = sliders['Stereo Pan'] / 50 - 1;
          stereoPannerRef.current.pan.setValueAtTime(
            stereoPan,
            targetAudio.context.currentTime
          );
        }
      }

      if (checkboxes['Bypass Effects']) {
        targetAudio.filters = [];
      } else {
        targetAudio.filters = selectedObject.audio_settings?.filters || [];
      }
      if (checkboxes['Bypass Listener Effects']) {
        try {
          targetAudio.setFilter(null);
        } catch (error) {
          if (
            !error.message.includes('disconnect') &&
            !error.message.includes('not connected')
          ) {
            console.warn('Audio filter error:', error);
          }
        }
      } else {
        try {
          targetAudio.setFilter(audioListenerRef.current?.filters || null);
        } catch (error) {
          if (
            !error.message.includes('disconnect') &&
            !error.message.includes('not connected')
          ) {
            console.warn('Audio filter error:', error);
          }
        }
      }
      if (checkboxes['Bypass Reverb Zones']) {
        if (
          targetAudio.reverb &&
          typeof targetAudio.reverb.setWet === 'function'
        ) {
          targetAudio.reverb.setWet(0);
        }
      }
      targetAudio.priority = sliders.Priority;
      if (typeof targetAudio.setPriority === 'function') {
        targetAudio.setPriority(sliders.Priority);
      } else {
        if (!targetAudio.userData || Object.isFrozen(targetAudio.userData)) {
          try {
            targetAudio.userData = { ...(targetAudio.userData || {}) };
          } catch (e) {
            console.warn('Could not clone targetAudio.userData:', e);
          }
        }
        try {
          targetAudio.userData.priority = sliders.Priority;
        } catch (e) {
          console.warn('Could not set targetAudio.userData.priority:', e);
        }
      }
      const stereoPan = sliders['Stereo Pan'] / 50 - 1;
      if (stereoPannerRef.current) {
        stereoPannerRef.current.pan.setValueAtTime(
          stereoPan,
          targetAudio.context.currentTime
        );
      }
      if (isPositional) {
        targetAudio.setDistanceModel(
          spatialSound === 'Short'
            ? 'linear'
            : spatialSound === 'Medium'
              ? 'exponential'
              : spatialSound === 'Long'
                ? 'inverse'
                : 'exponential'
        );
      }
      if (
        window_type === 'design' &&
        checkboxes['Play On Awake'] &&
        !targetAudio.isPlaying &&
        audioSource !== 'None (Audio Clip)'
      ) {
        targetAudio.play();
      }
    }, 100),
    [
      window_type,
      selectedObject,
      isAudioSourceValid,
      checkboxes.Loop,
      checkboxes.Mute,
      checkboxes['Play On Awake'],
      checkboxes['Bypass Effects'],
      checkboxes['Bypass Listener Effects'],
      checkboxes['Bypass Reverb Zones'],
      sliders.Volume,
      sliders.Pitch,
      sliders['Spatial Blend'],
      sliders['Reverb Zone Mix'],
      sliders.Priority,
      sliders['Stereo Pan'],
      spatialSound,
      audioSource,
      getVolumeFromSlider,
      audioListenerRef,
    ]
  );

  // Stop design-mode audio when entering preview (preview uses audioPlaybackUtils)
  useEffect(() => {
    if (!selectedObject || window_type !== 'preview') return;
    const targetAudio = selectedObject.getObjectByName?.(
      `audio_${selectedObject.uuid}`
    );
    if (!targetAudio?.isPlaying) return;
    try {
      targetAudio.stop();
    } catch (_) {
      /* ignore */
    }
  }, [window_type, selectedObject]);

  useEffect(() => {
    if (window_type !== 'design') return;
    updateAudioParameters();
  }, [
    window_type,
    checkboxes.Loop,
    checkboxes.Mute,
    checkboxes['Play On Awake'],
    checkboxes['Bypass Effects'],
    checkboxes['Bypass Listener Effects'],
    checkboxes['Bypass Reverb Zones'],
    sliders.Volume,
    sliders.Pitch,
    sliders['Spatial Blend'],
    sliders['Reverb Zone Mix'],
    sliders.Priority,
    sliders['Stereo Pan'],
    spatialSound,
    updateAudioParameters,
  ]);

  // Audio initialization and cleanup
  useEffect(() => {
    if (
      !completeAudioObject ||
      !audioLoader.current ||
      !selectedObject ||
      !isAudioSourceValid
    )
      return;

    const ThreeLib = completeAudioObject.Three || THREE;
    const { camera } = completeAudioObject;

    if (!audioListenerRef.current) {
      audioListenerRef.current = new ThreeLib.AudioListener();
      camera?.add(audioListenerRef.current);
    }

    if (audioSource !== 'None (Audio Clip)') {
      let isComponentMounted = true;

      let targetAudio = selectedObject.getObjectByName(
        `audio_${selectedObject.uuid}`
      );

      const spatialBlendValue = sliders['Spatial Blend'] || 0;
      const usePositional = spatialBlendValue > 0;
      const audioClass = usePositional
        ? ThreeLib.PositionalAudio
        : ThreeLib.Audio;

      // Track whether we need a full reload (url changed) or just a node-type swap.
      const needsUrlReload = !targetAudio || targetAudio.url !== audioSource;
      let wasPlaying = targetAudio?.isPlaying ?? false;

      // If the existing node is the wrong type (2D ↔ 3D switch), tear it down.
      // Preserve the wasPlaying flag so we can resume after the new node is ready.
      if (targetAudio && !(targetAudio instanceof audioClass)) {
        wasPlaying = targetAudio.isPlaying;
        try { targetAudio.stop(); } catch (_) {}
        ensureChildrenExtensible(selectedObject);
        selectedObject.remove(targetAudio);
        targetAudio = null;
      }

      // needsUrlReload was evaluated before the swap above, so we must also check
      // targetAudio === null (node was just removed for a type-swap).
      const shouldRebuild = needsUrlReload || !targetAudio;

      if (shouldRebuild) {
        // Remove any stale node that survived the type-check (url reload case).
        if (targetAudio) {
          wasPlaying = targetAudio.isPlaying;
          try { targetAudio.stop(); } catch (_) {}
          ensureChildrenExtensible(selectedObject);
          selectedObject.remove(targetAudio);
        }

        targetAudio = new audioClass(audioListenerRef.current);
        targetAudio.name = `audio_${selectedObject.uuid}`;
        targetAudio.url = audioSource;
        ensureChildrenExtensible(selectedObject);
        selectedObject.add(targetAudio);

        audioLoader.current.load(
          audioSource,
          (buffer) => {
            if (!isComponentMounted) return;
            targetAudio.setBuffer(buffer);
            if (targetAudio.context && targetAudio.gain) {
              if (stereoPannerRef.current) {
                try {
                  stereoPannerRef.current.disconnect();
                } catch (e) {}
              }
              const panner = targetAudio.context.createStereoPanner();
              targetAudio.gain.disconnect();
              targetAudio.gain.connect(panner);
              panner.connect(targetAudio.context.destination);
              stereoPannerRef.current = panner;
            }
            if (wasPlaying) {
              try {
                targetAudio.play();
              } catch (e) {}
            }
            updateAudioParameters();
          },
          null,
          (error) => {
            if (!isComponentMounted) return;
            console.error('Audio loading error:', error);
            completeAudioObject.toast?.error(
              'Failed to load audio file. Please ensure the file is valid and accessible.'
            );
            setIsAudioSourceValid(false);
          }
        );
      } else {
        // Node exists with the correct type — just update parameters in-place.
        updateAudioParameters();
      }

      return () => {
        isComponentMounted = false;
      };
    }
  }, [
    audioSource,
    completeAudioObject,
    selectedObject,
    isAudioSourceValid,
    updateAudioParameters,
    sliders['Spatial Blend'],
  ]);

  // Ensure audio is stopped and removed on unmount
  useEffect(() => {
    return () => {
      if (!selectedObject) return;
      const targetAudio = selectedObject.getObjectByName(
        `audio_${selectedObject.uuid}`
      );
      if (targetAudio) {
        try {
          targetAudio.stop();
        } catch (e) {}
        if (
          selectedObject.children &&
          selectedObject.children.includes(targetAudio) &&
          targetAudio.parent === selectedObject
        ) {
          try {
            ensureChildrenExtensible(selectedObject);
            selectedObject.remove(targetAudio);
          } catch (err) {
            console.warn(
              'Failed to remove targetAudio from selectedObject:',
              err
            );
          }
        }
      }
    };
  }, [selectedObject]);

  useEffect(() => {
    const handleMouseUp = () => {
      setSlidersWithTooltip({});
      document.body.style.userSelect = '';
    };

    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Handle file upload - uses same handleAudioUpload as UploadSection
  const handleFileChange = useCallback(
    (event) => {
      if (!event.target.files?.[0]) return;
      handleAudioUpload(
        event,
        completeAudioObject,
        completeAudioObject?.setPendingUploads
      );
    },
    [completeAudioObject]
  );

  // Early return for error states
  const errorCheck = useMemo(() => {
    if (!completeAudioObject) {
      return (
        <div className="bg-[#131418] text-xs p-2">
          Error: Audio configuration is missing
        </div>
      );
    }

    if (!completeAudioObject.Three && !THREE) {
      return (
        <div className="bg-[#131418] text-xs p-2">
          Error: Three.js library is missing. Audio functionality is disabled.
        </div>
      );
    }

    return null;
  }, [completeAudioObject]);

  if (errorCheck) return errorCheck;

  const checkboxList = useMemo(
    () =>
      Object.keys(checkboxes).map((label) => (
        <ToggleCheckbox
          key={label}
          label={label}
          checked={checkboxes[label]}
          onToggle={() => {
            setCheckboxes((prev) => ({
              ...prev,
              [label]: !prev[label],
            }));
          }}
        />
      )),
    [checkboxes]
  );

  const sliderList = useMemo(
    () =>
      Object.keys(liveSliders).map((label) => {
        const isVolume = label === 'Volume';
        const isPriority = label === 'Priority';
        const isPitch = label === 'Pitch';
        const isStereoPan = label === 'Stereo Pan';
        const isSpatialBlend = label === 'Spatial Blend';
        const isReverbZoneMix = label === 'Reverb Zone Mix';

        let value;
        if (isVolume) {
          value = Number((liveSliders[label] / 100).toFixed(3));
        } else if (isPriority) {
          value = liveSliders[label];
        } else if (isPitch) {
          value = Number(((liveSliders[label] / 100) * 6 - 3).toFixed(3));
        } else if (isStereoPan) {
          value = Number((liveSliders[label] / 50 - 1).toFixed(3));
        } else if (isSpatialBlend) {
          value = Number((liveSliders[label] / 100).toFixed(3));
        } else if (isReverbZoneMix) {
          value = Number(((liveSliders[label] / 100) * 1.1).toFixed(3));
        } else {
          value = liveSliders[label];
        }

        const handleSliderChange = (newValue) => {
          let normalizedValue;
          if (isVolume) {
            normalizedValue = Number((newValue * 100).toFixed(2));
          } else if (isPriority) {
            normalizedValue = newValue;
          } else if (isPitch) {
            normalizedValue = Number((((newValue + 3) / 6) * 100).toFixed(2));
          } else if (isStereoPan) {
            normalizedValue = Number(((newValue + 1) * 50).toFixed(2));
          } else if (isSpatialBlend) {
            normalizedValue = Number((newValue * 100).toFixed(2));
          } else if (isReverbZoneMix) {
            normalizedValue = Number(((newValue / 1.1) * 100).toFixed(2));
          } else {
            normalizedValue = newValue;
          }

          setLiveSliders((prev) => {
            const updated = { ...prev, [label]: normalizedValue };
            liveSlidersRef.current = updated;
            debouncedSetSliders(updated);
            return updated;
          });
        };

        return (
          <EnhancedSlider
            key={label}
            label={label}
            value={value}
            onChange={handleSliderChange}
            min={
              isVolume || isSpatialBlend
                ? 0
                : isPriority
                  ? 0
                  : isPitch
                    ? -3
                    : isStereoPan
                      ? -1
                      : isReverbZoneMix
                        ? 0
                        : 0
            }
            max={
              isVolume || isSpatialBlend
                ? 1
                : isPriority
                  ? 256
                  : isPitch
                    ? 3
                    : isStereoPan
                      ? 1
                      : isReverbZoneMix
                        ? 1.1
                        : 100
            }
            step={
              isVolume
                ? 0.01
                : isPriority
                  ? 1
                  : isPitch
                    ? 0.1
                    : isStereoPan
                      ? 0.01
                      : isSpatialBlend
                        ? 0.01
                        : isReverbZoneMix
                          ? 0.01
                          : 1
            }
            toFix={
              isVolume
                ? 2
                : isPriority
                  ? 0
                  : isPitch
                    ? 1
                    : isStereoPan
                      ? 2
                      : isSpatialBlend
                        ? 2
                        : isReverbZoneMix
                          ? 2
                          : undefined
            }
            displayValue={
              isVolume
                ? value.toFixed(2)
                : isPriority
                  ? value.toFixed(0)
                  : isPitch
                    ? value.toFixed(1)
                    : isStereoPan
                      ? value.toFixed(2)
                      : isSpatialBlend
                        ? value.toFixed(2)
                        : isReverbZoneMix
                          ? value.toFixed(2)
                          : undefined
            }
            minLabel={
              isPriority
                ? 'High'
                : isStereoPan
                  ? 'Left'
                  : isSpatialBlend
                    ? '2D'
                    : ''
            }
            maxLabel={
              isPriority
                ? 'Low'
                : isStereoPan
                  ? 'Right'
                  : isSpatialBlend
                    ? '3D'
                    : ''
            }
          />
        );
      }),
    [liveSliders, getVolumeFromSlider, debouncedSetSliders]
  );

  // Defensive: Ensure slider values are always numbers and within expected ranges
  useEffect(() => {
    const fixedSliders = { ...sliders };
    let changed = false;
    Object.keys(fixedSliders).forEach((key) => {
      let v = fixedSliders[key];
      if (typeof v !== 'number' || isNaN(v)) {
        v = 0;
        changed = true;
      }
      if (key === 'Volume' || key === 'Spatial Blend')
        v = Math.max(0, Math.min(100, v));
      if (key === 'Priority') v = Math.max(0, Math.min(256, v));
      if (key === 'Pitch') v = Math.max(0, Math.min(100, v));
      if (key === 'Stereo Pan') v = Math.max(0, Math.min(100, v));
      if (key === 'Reverb Zone Mix') v = Math.max(0, Math.min(100, v));
      if (fixedSliders[key] !== v) {
        fixedSliders[key] = v;
        changed = true;
      }
    });
    if (changed) {
      setSliders(fixedSliders);
      setLiveSliders(fixedSliders);
    }
  }, [sliders]);

  return (
    <div className="bg-[#131418] text-xs">
      {/* Audio Source Control */}
      <div className="flex justify-between items-center mb-2">
        <span className="text-[10px] text-gray-400 w-32">Audio Source</span>
        <div className="flex items-center">
          <div className="bg-[#2B2C2F] text-gray-400 px-2 py-1 text-[10px] mr-2 rounded w-16 truncate">
            {audioSource}
          </div>
          <div
            className="w-4 h-4 flex items-center justify-center cursor-pointer"
            onClick={() => fileInputRef.current.click()}
          >
            <img
              src={addcomponent.group306}
              className="h-3 w-3"
              alt="Add audio"
            />
          </div>
          <input
            type="file"
            ref={fileInputRef}
            accept="audio/mpeg,audio/wav,.mp3,.wav"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>
      </div>

      {/* Spatial Sound Control */}
      <GridDropdown
        label="Spatial Sound"
        value={spatialSound}
        onChange={(e) => {
          setSpatialSound(e.target.value);
        }}
        options={[
          {
            label: 'Select a parameter',
            value: 'Select a parameter',
            dis: true,
          },
          { label: 'Short', value: 'Short' },
          { label: 'Medium', value: 'Medium' },
          { label: 'Long', value: 'Long' },
        ]}
      />

      {/* Checkboxes */}
      {checkboxList}

      {/* Sliders */}
      {sliderList}
    </div>
  );
};

// Use memo to prevent re-rendering when props haven't changed
export default React.memo(AudioComponent, (prevProps, nextProps) => {
  const prevObj = prevProps.completeAudioObject?.selectedObject;
  const nextObj = nextProps.completeAudioObject?.selectedObject;
  return (
    prevObj?.uuid === nextObj?.uuid &&
    prevObj?.asset_file_path === nextObj?.asset_file_path &&
    prevObj?.objectURL === nextObj?.objectURL
  );
});

// Utility to clone an AudioBuffer (Web Audio API has no clone method)
function cloneAudioBuffer(audioBuffer, audioContext) {
  const channels = audioBuffer.numberOfChannels;
  const newBuffer = audioContext.createBuffer(
    channels,
    audioBuffer.length,
    audioBuffer.sampleRate
  );
  for (let i = 0; i < channels; i++) {
    newBuffer.copyToChannel(audioBuffer.getChannelData(i), i);
  }
  return newBuffer;
}

// Utility to swap AudioBufferSourceNode for pitch direction change, with offset
function swapAudioSource(
  targetAudio,
  newBuffer,
  playbackRate,
  isPlaying,
  audioContext,
  offset = 0
) {
  if (targetAudio.source) {
    try {
      targetAudio.source.stop();
    } catch (e) {}
    try {
      targetAudio.source.disconnect();
    } catch (e) {}
  }
  const newSource = audioContext.createBufferSource();
  newSource.buffer = newBuffer;
  newSource.playbackRate.value = playbackRate;
  if (targetAudio.gain) {
    newSource.connect(targetAudio.gain);
  } else {
    newSource.connect(audioContext.destination);
  }
  targetAudio.source = newSource;
  if (isPlaying) {
    try {
      newSource.start(0, offset);
    } catch (e) {
      newSource.start();
    }
  }
}
